import React, { useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import { styled } from '@mui/material';

import { CourseOverviewTab, MembersTab, SettingsTab } from 'components/organisms/CourseDashboard';
import { Box, Button, Container, Icon, Panel, Stack, Tabs, Text } from 'shell/components';

const Tablet = ({
    isLoading,
    courses,
    activeCourse,
    setActiveCourse,
    startCourseSetup,
    openDeleteCourseDialog,
    role,
    project,
    numGroups,
    selectProject,
    setProjectSettings,
    accountUsers,
    sendInvitations,
    removeUser,
    updateCourseSettings,
    handleProjectCardDropdown,
    handleGenerateId,
}) => {
    const tabOptions = [
        {
            key: 'overview',
            value: 'Course Overview',
            content: (
                <CourseOverviewTab
                    device={'tablet'}
                    course={activeCourse}
                    role={role}
                    project={project}
                    numGroups={numGroups}
                    selectProject={selectProject}
                    setProjectSettings={setProjectSettings}
                    handleProjectCardDropdown={handleProjectCardDropdown}
                    handleGenerateId={handleGenerateId}
                />
            ),
        },
        {
            key: 'members',
            value: 'Members',
            content: (
                <MembersTab
                    isInCourseDashboard={true}
                    course={activeCourse}
                    accountUsers={accountUsers}
                    sendInvitations={sendInvitations}
                    removeUser={removeUser}
                />
            ),
        },
        {
            key: 'settings',
            value: 'Settings',
            content: (
                <SettingsTab
                    device={'tablet'}
                    course={activeCourse}
                    updateCourseSettings={updateCourseSettings}
                    openDeleteCourseDialog={openDeleteCourseDialog}
                />
            ),
        },
    ];

    const [tab, setTab] = useState(tabOptions[0].key);

    const renderContent = (course) => {
        if (isLoading) return <LogoLoading />;

        if (!course.finished_course_set_up && role === 'instructor') {
            return (
                <Stack sx={{ mt: '30vh' }} alignItems={'center'} spacing={3}>
                    <Text variant={'h6'}>Your course set-up is in progress.</Text>
                    <Button variant={'primary'} onClick={selectProject}>
                        Continue with Set-Up
                    </Button>
                    <Button variant={'tertiary'} onClick={() => openDeleteCourseDialog(course)}>
                        Delete Course
                    </Button>
                </Stack>
            );
        }

        return (
            <CustomBox>
                {role === 'instructor' ? (
                    <Tabs aria-label={'select tab'} options={tabOptions} selected={tab} onChange={setTab} />
                ) : (
                    <CourseOverviewTab
                        device={'tablet'}
                        course={course}
                        role={role}
                        project={project}
                        numGroups={numGroups}
                        selectProject={selectProject}
                        setProjectSettings={setProjectSettings}
                    />
                )}
            </CustomBox>
        );
    };

    const courseOptions = (courses || []).map((course) => {
        return {
            ...course,
            key: course.course_id,
            value: course.name,
            content: renderContent(course),
        };
    });

    if (!courses) return <LogoLoading />;

    if (!courses.length) {
        return (
            <Stack sx={{ mt: '32vh' }} alignItems={'center'} spacing={4}>
                <Text variant={'h6'}>Looks like you don’t have any active courses yet.</Text>
                <Text variant={'p2'}>Create a course, wait for an invitation to join one.</Text>
                <Button onClick={startCourseSetup}>Create Course</Button>
            </Stack>
        );
    }

    return (
        <Container device={'tablet'}>
            <CustomPanel
                label={'Courses'}
                options={courseOptions}
                renderTab={(course) => (
                    <Stack sx={{ p: 2 }} alignItems={'flex-start'} spacing={1}>
                        <Text align={'left'} variant={'h6'} weight={'semibold'}>
                            {course.name || 'New Course'}
                        </Text>
                        <Text color={'light'} variant={'p2'}>
                            {course.semester}
                        </Text>
                        {course.role === 'instructor' && !course.finished_course_set_up ? (
                            <Stack direction={'row'} spacing={1}>
                                <CustomIcon kind={'fileTypeUndefined'} />
                                <Text color={'light'} variant={'p2'}>
                                    Draft
                                </Text>
                            </Stack>
                        ) : null}
                        {course.role === 'student' && !course.group_id ? (
                            <Stack direction={'row'} spacing={1}>
                                <CustomIcon kind={'clock'} />
                                <Text color={'light'} variant={'p2'}>
                                    Waiting for Instructor
                                </Text>
                            </Stack>
                        ) : null}
                    </Stack>
                )}
                selected={activeCourse?.course_id}
                onAdd={startCourseSetup}
                onChange={(courseId) => setActiveCourse(courses.find(({ course_id }) => course_id === courseId))}
            />
        </Container>
    );
};

const CustomPanel = styled(Panel)(({ theme }) => {
    return {
        minHeight: 'calc(100vh - 64px - 20px)',
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        fontSize: `${theme.typography.p2.fontSize.large}rem`,
        color: theme.palette.ensightful.neutral[600],
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default Tablet;
